import { useState, useEffect } from 'react';
import type { DentalOffice } from '../../types';

interface OfficeProfileFormProps {
  office: DentalOffice;
  onSubmit: (data: Partial<DentalOffice>) => void;
  isLoading?: boolean;
}

export function OfficeProfileForm({ office, onSubmit, isLoading }: OfficeProfileFormProps) {
  // Parse settings from JSON
  const settings = office.settings_json ? JSON.parse(office.settings_json) : {};

  const [formData, setFormData] = useState({
    name: office.name,
    address: office.address || '',
    phone: office.phone || '',
    email: office.email || '',
    business_hours: settings.business_hours || '',
    timezone: settings.timezone || 'Atlantic/Reykjavik',
    language: settings.language || 'is',
    currency: settings.currency || 'ISK',
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Check if form has changes
    const changed =
      formData.name !== office.name ||
      formData.address !== (office.address || '') ||
      formData.phone !== (office.phone || '') ||
      formData.email !== (office.email || '') ||
      formData.business_hours !== (settings.business_hours || '') ||
      formData.timezone !== (settings.timezone || 'Atlantic/Reykjavik') ||
      formData.language !== (settings.language || 'is') ||
      formData.currency !== (settings.currency || 'ISK');

    setHasChanges(changed);
  }, [formData, office, settings]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Office Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Nafn stofnunar *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Subdomain (readonly) */}
      <div>
        <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-1">
          Lén
        </label>
        <input
          type="text"
          id="subdomain"
          value={`${office.subdomain}.dental.myx.is`}
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
        />
        <p className="mt-1 text-xs text-gray-500">Ekki hægt að breyta léni</p>
      </div>

      {/* Address */}
      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
          Heimilisfang
        </label>
        <input
          type="text"
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Laugavegur 1, 101 Reykjavík"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Phone and Email Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Símanúmer
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="5551234"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Netfang
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="info@dental.is"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Business Hours */}
      <div>
        <label htmlFor="business_hours" className="block text-sm font-medium text-gray-700 mb-1">
          Opnunartími
        </label>
        <textarea
          id="business_hours"
          name="business_hours"
          value={formData.business_hours}
          onChange={handleChange}
          rows={3}
          placeholder="Mán-Fös: 09:00-17:00"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Timezone, Language, Currency Grid */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
            Tímabelti
          </label>
          <select
            id="timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Atlantic/Reykjavik">Reykjavík (GMT)</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Oslo">Oslo (CET)</option>
            <option value="America/New_York">New York (EST)</option>
          </select>
        </div>

        <div>
          <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
            Tungumál
          </label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="is">Íslenska</option>
            <option value="en">English</option>
          </select>
        </div>

        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
            Gjaldmiðill
          </label>
          <select
            id="currency"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="ISK">ISK (kr)</option>
            <option value="EUR">EUR (€)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t">
        <button
          type="submit"
          disabled={!hasChanges || isLoading}
          className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Vistar...' : 'Vista breytingar'}
        </button>
      </div>
    </form>
  );
}
